/**
 * executionMetricsService.ts
 * Calcula e persiste KPIs automáticos ao concluir uma task.
 * Gera alertas, problemas (MASP) e notificações quando necessário.
 */
import { supabase } from '../../../lib/supabaseClient';
import type { ExecutionChecklistItem, ExecutionField, StageRule } from '../../../types/card';
import { evaluateRule } from './executionEngine';

// ============================================================================
// TIPOS
// ============================================================================

export interface TaskMetricsInput {
  taskId: string;
  stageId: string;
  instanceId: string;
  organizationId: string;
  workflowTemplateId?: string;
  completedBy: string;
  assignedRoleId?: string;

  // Timestamps operacionais
  enteredAt?: string | null;
  claimedAt?: string | null;
  startedAt?: string | null;
  completedAt: string;

  // Dados de execução
  formFields: ExecutionField[];
  formData: Record<string, unknown>;
  checklist: ExecutionChecklistItem[];
  checklistData: Record<string, boolean>;
  attachmentCount: number;
  rules?: StageRule[];

  // SLA
  slaHours?: number | null;

  // Reprocesso
  reworkCount?: number;
}

export interface TaskMetricsResult {
  leadTimeSeconds: number;
  touchTimeSeconds: number;
  queueTimeSeconds: number;
  qualityScore: number;
  checklistCompletionRate: number;
  fieldsCompletionRate: number;
  slaStatus: 'on_time' | 'late' | 'critical';
  slaDeviationPercent: number | null;
  validationErrorsCount: number;
  errorFlag: boolean;
}

// ============================================================================
// CÁLCULO DE MÉTRICAS
// ============================================================================

export function calculateTaskMetrics(input: TaskMetricsInput): TaskMetricsResult {
  const now = new Date(input.completedAt);

  // ── Tempos ──
  const entered = input.enteredAt ? new Date(input.enteredAt) : null;
  const claimed = input.claimedAt ? new Date(input.claimedAt) : null;
  const started = input.startedAt ? new Date(input.startedAt) : null;

  const leadTimeSeconds = entered
    ? Math.round((now.getTime() - entered.getTime()) / 1000)
    : started
      ? Math.round((now.getTime() - started.getTime()) / 1000)
      : 0;

  const touchTimeSeconds = started
    ? Math.round((now.getTime() - started.getTime()) / 1000)
    : 0;

  const queueTimeSeconds = entered && (claimed ?? started)
    ? Math.round(((claimed ?? started)!.getTime() - entered.getTime()) / 1000)
    : 0;

  // ── %C&A (Qualidade) ──
  const ca = calculateCA(input);

  // ── SLA ──
  const slaResult = calculateSlaStatus(input.slaHours, leadTimeSeconds);

  // ── Regras violadas ──
  let validationErrorsCount = 0;
  if (input.rules) {
    for (const rule of input.rules) {
      const result = evaluateRule(rule, input.formData);
      if (result.triggered && (rule.action === 'block_completion' || rule.type === 'error')) {
        validationErrorsCount++;
      }
    }
  }

  const errorFlag = ca.qualityScore < 100 || slaResult.status !== 'on_time' || validationErrorsCount > 0;

  return {
    leadTimeSeconds,
    touchTimeSeconds,
    queueTimeSeconds,
    qualityScore: ca.qualityScore,
    checklistCompletionRate: ca.checklistRate,
    fieldsCompletionRate: ca.fieldsRate,
    slaStatus: slaResult.status,
    slaDeviationPercent: slaResult.deviationPercent,
    validationErrorsCount,
    errorFlag,
  };
}

// ============================================================================
// %C&A — Complete & Accurate
// ============================================================================

function calculateCA(input: TaskMetricsInput): {
  qualityScore: number;
  checklistRate: number;
  fieldsRate: number;
} {
  // Campos obrigatórios preenchidos
  const requiredFields = input.formFields.filter(f => f.required);
  const filledRequired = requiredFields.filter(f => {
    const v = input.formData[f.id];
    return v !== undefined && v !== null && v !== '';
  });
  const fieldsRate = requiredFields.length > 0
    ? Math.round((filledRequired.length / requiredFields.length) * 100)
    : 100;

  // Checklist obrigatório completo
  const requiredChecks = input.checklist.filter(c => c.required !== false);
  const completedChecks = requiredChecks.filter(c => input.checklistData[c.id]);
  const checklistRate = requiredChecks.length > 0
    ? Math.round((completedChecks.length / requiredChecks.length) * 100)
    : 100;

  // Score composto (média ponderada: 60% campos, 40% checklist)
  const qualityScore = requiredFields.length > 0 || requiredChecks.length > 0
    ? Math.round(fieldsRate * 0.6 + checklistRate * 0.4)
    : 100;

  return { qualityScore, checklistRate, fieldsRate };
}

// ============================================================================
// SLA
// ============================================================================

function calculateSlaStatus(
  slaHours: number | null | undefined,
  leadTimeSeconds: number,
): { status: 'on_time' | 'late' | 'critical'; deviationPercent: number | null } {
  if (!slaHours || slaHours <= 0) {
    return { status: 'on_time', deviationPercent: null };
  }

  const slaSeconds = slaHours * 3600;
  const deviationPercent = Math.round(((leadTimeSeconds - slaSeconds) / slaSeconds) * 100);

  if (leadTimeSeconds <= slaSeconds) {
    return { status: 'on_time', deviationPercent };
  }
  if (leadTimeSeconds <= slaSeconds * 1.5) {
    return { status: 'late', deviationPercent };
  }
  return { status: 'critical', deviationPercent };
}

// ============================================================================
// PERSISTÊNCIA — Salvar métricas no banco
// ============================================================================

export async function saveTaskMetrics(
  input: TaskMetricsInput,
  metrics: TaskMetricsResult,
): Promise<void> {
  const { error } = await supabase
    .from('kpi_execution_metrics_v2')
    .upsert({
      task_id: input.taskId,
      organization_id: input.organizationId,
      stage_id: input.stageId,
      instance_id: input.instanceId,
      workflow_template_id: input.workflowTemplateId ?? null,
      lead_time_seconds: metrics.leadTimeSeconds,
      touch_time_seconds: metrics.touchTimeSeconds,
      queue_time_seconds: metrics.queueTimeSeconds,
      wait_time_seconds: metrics.leadTimeSeconds - metrics.touchTimeSeconds - metrics.queueTimeSeconds,
      sla_hours: input.slaHours ?? null,
      sla_status: metrics.slaStatus,
      sla_deviation_percent: metrics.slaDeviationPercent,
      quality_score: metrics.qualityScore,
      checklist_completion_rate: metrics.checklistCompletionRate,
      fields_completion_rate: metrics.fieldsCompletionRate,
      validation_errors_count: metrics.validationErrorsCount,
      attachments_count: input.attachmentCount,
      rework_count: input.reworkCount ?? 0,
      error_flag: metrics.errorFlag,
      completed_by: input.completedBy,
      assigned_role_id: input.assignedRoleId ?? null,
      entered_at: input.enteredAt ?? null,
      claimed_at: input.claimedAt ?? null,
      started_at: input.startedAt ?? null,
      completed_at: input.completedAt,
    }, { onConflict: 'task_id' });

  if (error) {
    console.error('[metrics] saveTaskMetrics error:', error.message);
  }
}

// ============================================================================
// AUTO-PROBLEMA (MASP) — Gerar issue quando qualidade falha
// ============================================================================

export async function generateAutoProblems(
  input: TaskMetricsInput,
  metrics: TaskMetricsResult,
  stageName: string,
): Promise<void> {
  const problems: { title: string; type: string; severity: string }[] = [];

  if (metrics.slaStatus === 'critical') {
    problems.push({
      title: `SLA crítico na etapa "${stageName}"`,
      type: 'sla_violation',
      severity: 'critical',
    });
  } else if (metrics.slaStatus === 'late') {
    problems.push({
      title: `SLA estourado na etapa "${stageName}"`,
      type: 'sla_violation',
      severity: 'high',
    });
  }

  if (metrics.qualityScore < 80) {
    problems.push({
      title: `Qualidade abaixo do aceitável na etapa "${stageName}" (%C&A: ${metrics.qualityScore}%)`,
      type: 'quality_failure',
      severity: metrics.qualityScore < 50 ? 'critical' : 'high',
    });
  }

  if (metrics.validationErrorsCount > 0) {
    problems.push({
      title: `Regras violadas na etapa "${stageName}" (${metrics.validationErrorsCount} violação(ões))`,
      type: 'rule_violation',
      severity: 'medium',
    });
  }

  for (const prob of problems) {
    const { error } = await supabase
      .from('issues')
      .insert({
        organization_id: input.organizationId,
        title: prob.title,
        type: prob.type,
        severity: prob.severity,
        status: 'open',
        origin_task_id: input.taskId,
        origin_stage_id: input.stageId,
        origin_instance_id: input.instanceId,
        reported_by: input.completedBy,
        metadata: {
          auto_generated: true,
          quality_score: metrics.qualityScore,
          sla_status: metrics.slaStatus,
          sla_deviation_percent: metrics.slaDeviationPercent,
          lead_time_seconds: metrics.leadTimeSeconds,
        },
      });

    if (error) {
      console.error('[metrics] generateAutoProblems error:', error.message);
    }
  }

  // Log de eventos
  if (problems.length > 0) {
    await logIntelligenceEvent(input.organizationId, input.taskId, input.instanceId, input.stageId, 'problem_created', {
      problems_count: problems.length,
      types: problems.map(p => p.type),
    });
  }
}

// ============================================================================
// NOTIFICAÇÕES
// ============================================================================

export async function sendNotification(params: {
  organizationId: string;
  userId: string;
  type: string;
  severity?: 'info' | 'warning' | 'critical';
  title: string;
  message?: string;
  link?: string;
  sourceType?: string;
  sourceId?: string;
}): Promise<void> {
  const { error } = await supabase
    .from('notifications_v2')
    .insert({
      organization_id: params.organizationId,
      user_id: params.userId,
      type: params.type,
      severity: params.severity ?? 'info',
      title: params.title,
      message: params.message ?? null,
      link: params.link ?? null,
      source_type: params.sourceType ?? null,
      source_id: params.sourceId ?? null,
    });

  if (error) {
    console.error('[notifications] sendNotification error:', error.message);
  }
}

// ============================================================================
// ALERTAS — SLA breach + quality alert
// ============================================================================

export async function processAlerts(
  input: TaskMetricsInput,
  metrics: TaskMetricsResult,
  stageName: string,
  assignedUserId?: string,
): Promise<void> {
  if (!assignedUserId) return;

  if (metrics.slaStatus === 'critical') {
    await sendNotification({
      organizationId: input.organizationId,
      userId: assignedUserId,
      type: 'sla_breached',
      severity: 'critical',
      title: `SLA crítico: ${stageName}`,
      message: `Lead time ${Math.round(metrics.leadTimeSeconds / 60)}min — ${metrics.slaDeviationPercent}% acima do SLA`,
      link: `/minha-mesa?task=${input.taskId}`,
      sourceType: 'task',
      sourceId: input.taskId,
    });

    await logIntelligenceEvent(input.organizationId, input.taskId, input.instanceId, input.stageId, 'sla_breached', {
      sla_hours: input.slaHours,
      lead_time_seconds: metrics.leadTimeSeconds,
    });
  }

  if (metrics.qualityScore < 80) {
    await sendNotification({
      organizationId: input.organizationId,
      userId: assignedUserId,
      type: 'quality_alert',
      severity: 'warning',
      title: `Qualidade baixa: ${stageName}`,
      message: `%C&A: ${metrics.qualityScore}% — Campos: ${metrics.fieldsCompletionRate}%, Checklist: ${metrics.checklistCompletionRate}%`,
      link: `/minha-mesa?task=${input.taskId}`,
      sourceType: 'task',
      sourceId: input.taskId,
    });

    await logIntelligenceEvent(input.organizationId, input.taskId, input.instanceId, input.stageId, 'quality_alert', {
      quality_score: metrics.qualityScore,
    });
  }
}

// ============================================================================
// LOG INTELLIGENCE EVENT
// ============================================================================

async function logIntelligenceEvent(
  orgId: string,
  taskId: string,
  instanceId: string,
  stageId: string,
  event: string,
  metadata?: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase
    .from('execution_logs_v2')
    .insert({
      organization_id: orgId,
      task_id: taskId,
      instance_id: instanceId,
      stage_id: stageId,
      event,
      metadata: metadata ?? {},
    });

  if (error) {
    console.error('[intelligence] logEvent error:', error.message);
  }
}

// ============================================================================
// ORQUESTRADOR — Roda tudo ao concluir task
// ============================================================================

export async function processTaskCompletion(
  input: TaskMetricsInput,
  stageName: string,
  assignedUserId?: string,
): Promise<TaskMetricsResult> {
  // 1. Calcular métricas
  const metrics = calculateTaskMetrics(input);

  // 2. Salvar métricas (non-blocking errors)
  await saveTaskMetrics(input, metrics);

  // 3. Gerar problemas MASP se necessário
  await generateAutoProblems(input, metrics, stageName);

  // 4. Processar alertas e notificações
  await processAlerts(input, metrics, stageName, assignedUserId);

  // 5. Log evento de conclusão
  await logIntelligenceEvent(
    input.organizationId,
    input.taskId,
    input.instanceId,
    input.stageId,
    'task_completed',
    {
      lead_time_seconds: metrics.leadTimeSeconds,
      quality_score: metrics.qualityScore,
      sla_status: metrics.slaStatus,
      error_flag: metrics.errorFlag,
    },
  );

  return metrics;
}
